import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Drawer, Form, Select, Table,
} from 'antd';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

import BoardColorPicker from './BoardColorPicker';
import DrawerSubmitFooter from '../common/containers/DrawerSubmitFooter';
import FormTextInput from '../common/inputs/FormTextInput';

import BoardMoveWorkflowConfigure from './BoardMoveWorkflowConfigure';

import {
  getTypes,
  getTypeMap,
  TITLE_FIELD,
  TITLE_FIELD_ID,
  SUB_TYPE_MAP,
  DATE_FIELD_TYPES,
  isTimeTriggerType,
} from './boardWorkflowConfig';
import BoardWorkflowSubtasks from './BoardWorkflowSubtasks';
import DurationInput from '../common/inputs/DurationInput';
import UserAssignmentSelector from '../forms/FormWorkflows/selectors/UserAssignmentSelector';
import OnTraccrCheckbox from '../common/inputs/OnTraccrCheckbox';
import OnTraccrSwitch from '../common/inputs/OnTraccrSwitch';
import { getProjectFileTemplates } from '../projects/state/projects.actions';

const formLabelStyle = {
  style: {
    paddingBottom: 5,
    marginTop: 10,
  },
};

// eslint-disable-next-line no-useless-escape
const emailRegex = new RegExp(/^[^@\s]+@[^@\s\.]+(?:\.[^@\.\s]+)+$/);

const tokenSeparators = [',', '\n'];

const MISSING_DATE_FIELD_MESSAGE = 'This card design no longer has this date field.'
  + ' Select another one, or turn off "Use a date field on the card" to trigger off a duration instead.';

function BoardWorkflowAddDrawer({
  visible,
  onClose,
  onSubmit,
  cardTypeId,
  selectedWorkflow,
  currentStatuses = [],
}) {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const cardTemplates = useSelector((state) => state.boards.cardTemplates);
  const stateProjectTypes = useSelector((state) => state.projects.projectTypes);
  const projectFileTemplates = useSelector((state) => state.projects.projectFileTemplates);
  const selectedBoard = useSelector((state) => state.boards.selectedBoard);
  const boards = useSelector((state) => state.boards.boards);

  const [name, setName] = useState();
  const [selectedType, setSelectedType] = useState();
  const [mappings, setMappings] = useState({});
  const [customerMappings, setCustomerMappings] = useState({});
  const [projectMappings, setProjectMappings] = useState({});
  const [initialStatusId, setInitialStatusId] = useState();
  const [targetBoardId, setTargetBoardId] = useState();
  const [keepColor, setKeepColor] = useState(false);
  const [subTypes, setSubTypes] = useState([]);
  const [selectedSubType, setSelectedSubType] = useState();
  const [duration, setDuration] = useState();
  const [useDateField, setUseDateField] = useState(false);
  const [dateFieldId, setDateFieldId] = useState();
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [selectedProjectTypes, setSelectedProjectTypes] = useState([]);
  const [hasZapier, setHasZapier] = useState(false);
  const [checkedZapier, setCheckedZapier] = useState(false);

  const users = useSelector((state) => state.users.users);

  const userIds = useMemo(() => {
    const idSet = new Set();
    users.forEach((user) => {
      if (user.active) {
        idSet.add(user.id);
      }
    });
    return idSet;
  }, [users]);

  const positionNames = useSelector((state) => state.settings.positionNames);

  const positionIds = useMemo(() => {
    const idSet = new Set();
    positionNames.forEach((position) => {
      idSet.add(position.id);
    });
    return idSet;
  }, [positionNames]);

  const ourFields = useMemo(() => {
    const {
      [cardTypeId]: {
        fields: sections = [],
      } = {},
    } = cardTemplates;
    const formFields = sections.map((section) => {
      const { fields = [] } = section;
      return fields;
    }).flat();
    return [TITLE_FIELD].concat(formFields);
  }, [cardTemplates, cardTypeId]);

  // Date fields on the card design that can drive a time trigger dynamically
  const dateFieldOptions = useMemo(() => (
    ourFields
      .filter((field) => DATE_FIELD_TYPES.includes(field.selectedType))
      .map((field) => ({ label: field.configProps?.title, value: field.id }))
  ), [ourFields]);

  // True when date-field mode is on and either the design has no date fields at all
  // or the saved dateFieldId isn't among the remaining options.
  const isDateFieldMissing = useDateField && (
    !dateFieldOptions.length
    || (!!dateFieldId && !dateFieldOptions.some(({ value }) => value === dateFieldId))
  );

  const validateAndFixEmails = useCallback((selectedValues) => (
    selectedValues?.filter((emailOrId) => (
      userIds.has(emailOrId)
      || positionIds.has(emailOrId)
      || emailRegex.test(emailOrId)
    ))
  ), [userIds, positionIds]);

  const onValuesChanged = useCallback((_, fullData) => {
    const {
      type: newType,
      name: newName,
      initialStatusId: newStatusId,
      targetBoardId: newTargetBoardId,
      keepColor: newKeepColor,
      subType: newSubType,
      duration: newDuration,
      useDateField: newUseDateField,
      dateFieldId: newDateFieldId,
      emails: newEmails = [],
      projectTypes: newProjectTypes,
    } = fullData;

    setSelectedEmails(validateAndFixEmails(newEmails));

    const relevantSubTypes = SUB_TYPE_MAP[newType] ?? [];
    setSubTypes(relevantSubTypes);

    if (!relevantSubTypes.length) {
      setSelectedSubType();
    }

    setDuration(newDuration);
    setUseDateField(!!newUseDateField);
    setDateFieldId(newDateFieldId);
    setSelectedType(newType);
    setSelectedSubType(newSubType);
    setName(newName);
    setInitialStatusId(newStatusId);
    setKeepColor(newKeepColor);
    if (newTargetBoardId !== targetBoardId) {
      const newData = { ...fullData, targetStatusId: null };
      form.setFieldsValue(newData);
    }
    setTargetBoardId(newTargetBoardId);
    if (newType !== selectedType) {
      setMappings({});
      setCustomerMappings({});
      setProjectMappings({});
      form.setFieldsValue({ mappings: {}, customerMappings: {}, projectMappings: {} });
    }
    setSelectedProjectTypes(newProjectTypes);
  }, [selectedType, form, targetBoardId, validateAndFixEmails]);

  const onSubmitClicked = useCallback(async () => {
    try {
      const values = await form.validateFields();
      const newData = {
        mappings,
        customerMappings,
        projectMappings,
        type: selectedType,
        name,
        initialStatusId,
        color: values.color,
        subType: selectedSubType,
        emails: selectedEmails,
      };
      if (isTimeTriggerType(selectedType)) {
        newData.duration = useDateField ? null : duration;
        newData.dateFieldId = useDateField ? dateFieldId : null;
      }
      if (selectedType === 'project' || selectedType === 'projectTypeChange') {
        newData.projectTypes = selectedProjectTypes;
      }
      if (selectedType === 'moveBoard' || selectedType === 'copyCard') {
        newData.mappings = {
          targetBoardId: values.targetBoardId,
          targetStatusId: values.targetStatusId,
        };
        newData.keepColor = values.keepColor;
        newData.createShadow = !!values.createShadow;
      }
      if (!selectedWorkflow) newData.id = DateTime.local().toMillis();
      if (selectedType === 'statusTasks' || (isTimeTriggerType(selectedType) && selectedSubType === 'createTask')) {
        newData.tasks = values.tasks;
      }
      if (selectedType === 'createCustomerProject' || selectedType === 'statusCustomerProject') {
        newData.createProject = !!values.createProject;
        newData.createCustomer = !!values.createCustomer;
        if (values.createProject) {
          newData.projectFileTemplateId = values.projectFileTemplateId || null;
          newData.projectTypes = values.createProjectTypeId ? [values.createProjectTypeId] : [];
        }
      }

      onSubmit(newData);
    } catch (err) {
      //
    }
  }, [
    form,
    mappings,
    customerMappings,
    projectMappings,
    selectedType,
    name,
    selectedWorkflow,
    initialStatusId,
    duration,
    useDateField,
    dateFieldId,
    selectedSubType,
    selectedEmails,
    selectedProjectTypes,
  ]);

  useEffect(() => {
    if (!visible) {
      form.resetFields();
      setSelectedType();
      setMappings({});
      setCustomerMappings({});
      setProjectMappings({});
      setName();
      setInitialStatusId();
      setTargetBoardId();
      setKeepColor(false);
      setDuration();
      setUseDateField(false);
      setDateFieldId();
      setSubTypes([]);
      setSelectedSubType();
      setSelectedEmails([]);
      setSelectedProjectTypes([]);
    }
  }, [visible, form]);

  const divisionId = useMemo(() => {
    // Get the division ID from the selected board
    if (selectedBoard?.id && boards[selectedBoard.id]) {
      const boardDivisions = boards[selectedBoard.id].divisions;
      if (Array.isArray(boardDivisions) && boardDivisions.length > 0) {
        return boardDivisions;
      }
    }

    return [];
  }, [selectedBoard, boards]);

  // Filter templates by division
  const fileTemplateOptions = useMemo(() => {
    if (!projectFileTemplates) return [];

    const filtered = divisionId.length > 0
      ? projectFileTemplates.filter((t) => !t.divisionId || divisionId.includes(t.divisionId))
      : projectFileTemplates;

    return filtered
      .map((t) => ({ label: t.name, value: t.id }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [projectFileTemplates, divisionId]);

  // Load project file templates when drawer opens
  useEffect(() => {
    if (visible) {
      dispatch(getProjectFileTemplates());
    }
  }, [visible, dispatch]);

  useEffect(() => {
    if (!selectedWorkflow) return;
    const {
      type: newType,
      name: newName,
      mappings: newMappings,
      initialStatusId: newStatusId,
      keepColor: newKeepColor,
      emails: newEmails,
      subType: newSubType,
      duration: newDuration,
      dateFieldId: newDateFieldId,
      projectType: newProjectTypes,
      createShadow: newCreateShadow,
      createCustomer,
      createProject,
      customerMappings: newCustomerMappings,
      projectMappings: newProjectMappings,
      projectFileTemplateId,
    } = selectedWorkflow;
    setName(newName);
    setSelectedType(newType);
    setMappings(newMappings);
    setInitialStatusId(newStatusId);
    setSelectedProjectTypes(newProjectTypes);
    setCustomerMappings(newCustomerMappings);
    setProjectMappings(newProjectMappings);

    const formData = {
      ...selectedWorkflow,
      color: selectedWorkflow.color || '#FFFFFFFF',
      tasks: selectedWorkflow.tasks || [],
      keepColor: !!selectedWorkflow.keepColor,
      emails: [],
    };
    if (newType === 'moveBoard' || newType === 'copyCard') {
      formData.targetBoardId = (newMappings ?? {}).targetBoardId;
      formData.targetStatusId = (newMappings ?? {}).targetStatusId;
      formData.createShadow = newType === 'moveBoard' ? !!newCreateShadow : false;
      setTargetBoardId(formData.targetBoardId);
      setKeepColor(newKeepColor);
    }

    if (isTimeTriggerType(newType)) {
      formData.duration = newDuration;
      setDuration(newDuration);
      formData.dateFieldId = newDateFieldId;
      setDateFieldId(newDateFieldId);
      formData.useDateField = !!newDateFieldId;
      setUseDateField(!!newDateFieldId);
      setSubTypes(SUB_TYPE_MAP[newType] ?? []);
      setSelectedSubType(newSubType);

      if (newSubType === 'sendEmail') {
        formData.emails = newEmails;
        setSelectedEmails(newEmails);
      }
    }

    if (newType === 'createCustomerProject' || newType === 'statusCustomerProject') {
      formData.createCustomer = !!createCustomer;
      formData.createProject = !!createProject;
      formData.projectFileTemplateId = projectFileTemplateId;
      const [firstProjectType] = selectedWorkflow.projectTypes || [];
      formData.createProjectTypeId = firstProjectType ?? null;
    }

    form.setFieldsValue(formData);
  }, [selectedWorkflow, form]);

  const TYPE_MAP = getTypeMap(t);

  const CUSTOMER_FIELDS = TYPE_MAP.customer;
  const PROJECT_FIELDS = TYPE_MAP.project.slice(0, 14);

  const typeFields = useMemo(() => {
    const {
      [selectedType]: fields = [],
    } = TYPE_MAP;
    return fields;
  }, [selectedType]);

  const columns = useMemo(() => [
    {
      dataIndex: 'sourceField',
      title: 'Source Field',
      width: 300,
      render: (_, record) => {
        const { selectedType: fieldType, id, configProps: { dataType } = {} } = record;
        const options = typeFields.filter((field) => field.type === fieldType
          && (field.type !== 'dropdown' || dataType === field.dataType));
        const {
          [id]: value,
        } = mappings;
        return (
          <Select
            options={options}
            value={value}
            onSelect={(fieldKey) => {
              setMappings({
                ...mappings,
                [id]: fieldKey,
              });
            }}
          />
        );
      },
    }, {
      dataIndex: 'targetField',
      title: 'Card Field',
      width: 300,
      render: (_, record) => {
        const {
          configProps: { title } = {},
          id,
        } = record;
        const className = id === TITLE_FIELD_ID ? 'form-required-field' : null;
        return <span className={className}>{title}</span>;
      },
    },
  ], [ourFields, typeFields, mappings]);

  const customerColumns = [
    {
      dataIndex: 'sourceField',
      title: 'Card Field',
      width: 300,
      render: (_, record) => {
        const { value: customerField } = record;

        const options = ourFields
          .filter((field) => field.selectedType === record.type
          && (field.type !== 'dropdown' || record.dataType === field.dataType))
          .map((field) => {
            const { id: newId, configProps: { title: newTitle } = {} } = field;
            return { key: newId, value: newId, label: newTitle };
          });

        const {
          [customerField]: value,
        } = customerMappings;

        return (
          <Select
            options={options}
            value={value}
            onSelect={(fieldKey) => {
              setCustomerMappings({
                ...customerMappings,
                [customerField]: fieldKey,
              });
            }}
          />
        );
      },
    },
    {
      dataIndex: 'targetField',
      title: 'Customer Field',
      width: 300,
      render: (_, record) => {
        const { label, required } = record;
        const className = required ? 'form-required-field' : null;
        return <span className={className}>{label}</span>;
      },
    },
  ];

  const projectColumns = [
    {
      dataIndex: 'sourceField',
      title: 'Card Field',
      width: 300,
      render: (_, record) => {
        const { value: projectField } = record;

        const options = ourFields
          .filter((field) => (field.selectedType === record.type
          && (field.type !== 'dropdown' || record.dataType === field.dataType))
          || (field.selectedType === 'gpsLocation' && record.label === 'Address'))
          .map((field) => {
            const { id: newId, configProps: { title: newTitle } = {} } = field;
            return { key: newId, value: newId, label: newTitle };
          });

        const {
          [projectField]: value,
        } = projectMappings;

        return (
          <Select
            options={options}
            value={value}
            onSelect={(fieldKey) => {
              setProjectMappings({
                ...projectMappings,
                [projectField]: fieldKey,
              });
            }}
          />
        );
      },
    },
    {
      dataIndex: 'targetField',
      title: 'Project Field',
      width: 300,
      render: (_, record) => {
        const { label } = record;
        const className = (label === 'Name' || label === 'Address') ? 'form-required-field' : null;
        return <span className={className}>{label}</span>;
      },
    },
  ];

  const canSubmit = useMemo(() => {
    if (!name || !selectedType) return false;
    if (selectedType === 'moveBoard' || selectedType === 'copyCard') return !!targetBoardId;
    if (selectedType === 'statusTasks' || selectedType === 'zapierMove') return !!initialStatusId;
    if (isTimeTriggerType(selectedType)) {
      const hasTiming = useDateField
        ? (!!dateFieldId && !isDateFieldMissing)
        : !!(duration?.duration && duration?.durationUnit);
      if (!hasTiming || !selectedSubType || !initialStatusId) {
        return false;
      }

      if (selectedSubType === 'sendEmail') {
        return selectedEmails?.length;
      }
      return true;
    }

    if (selectedType === 'createCustomerProject' || selectedType === 'statusCustomerProject') {
      const isCustomerValid = form.getFieldValue('createCustomer')
        && customerMappings.name
        && customerMappings.officeNumber
        && customerMappings.officeEmail;

      const isProjectValid = form.getFieldValue('createProject')
        && projectMappings.name
        && projectMappings.address;

      // If both are checked, both must be valid
      if (form.getFieldValue('createCustomer') && form.getFieldValue('createProject')) {
        return !!(isCustomerValid && isProjectValid);
      }
      return !!(isCustomerValid || isProjectValid);
    }

    return !!(initialStatusId && mappings.cardTitle);
  }, [
    mappings,
    customerMappings,
    projectMappings,
    name,
    selectedType,
    initialStatusId,
    targetBoardId,
    duration,
    useDateField,
    isDateFieldMissing,
    dateFieldId,
    selectedSubType,
    selectedEmails,
  ]);

  const statusOptions = useMemo(() => (
    currentStatuses.map((status) => ({ label: status.title, value: status.id }))
  ), [currentStatuses]);

  let statusLabel = 'Initial Status';
  if (selectedType === 'notInStatus') {
    statusLabel = 'Target Status';
  } else if (selectedType === 'statusTasks' || isTimeTriggerType(selectedType)) {
    statusLabel = 'Status';
  }
  const durationLabel = selectedType === 'notInStatus' ? 'From Card Creation' : 'Time in Status';
  const translatedTypes = getTypes(t, hasZapier);

  const projectTypeOptions = useMemo(() => ([
    { key: -1, label: 'None', value: null },
    ...stateProjectTypes.map((type) => ({ label: type.name, value: type.id })),
  ]), [stateProjectTypes]);

  const isProjectTypeChange = selectedType === 'projectTypeChange';

  useEffect(() => {
    // Clear the timing fields when switching to a type that does not use them.
    // selectedType is only set once a type has been picked/loaded, so this does not
    // wipe the values an existing workflow just loaded into the form.
    if (selectedType && !isTimeTriggerType(selectedType)) {
      form.setFieldsValue({ duration: undefined, useDateField: false, dateFieldId: undefined });
      setUseDateField(false);
      setDateFieldId();
    }
  }, [selectedType, form]);

  useEffect(() => {
    const loadConfig = async () => {
      try {
        const { data: newConfig } = await axios.get('/zapier/connection');
        setHasZapier(!!newConfig?.apiKey);
      } catch (err) {
        setHasZapier(false);
      }
      setCheckedZapier(true);
    };
    if (visible && !checkedZapier) {
      loadConfig();
    }
  }, [visible, checkedZapier]);

  return (
    <Drawer
      title="Add Workflow"
      visible={visible}
      width={700}
      onClose={onClose}
      bodyStyle={{ padding: '0px 24px' }}
    >
      <Form
        layout="vertical"
        form={form}
        onValuesChange={onValuesChanged}
        style={{
          position: 'absolute', top: 53, bottom: 51, left: 24, right: 24, overflowY: 'auto',
        }}
        initialValues={{
          color: '#FFFFFFFF',
          tasks: [],
          createProjectTypeId: null,
          useDateField: false,
        }}
      >
        <FormTextInput
          name="name"
          label="Name"
          rules={[{ required: true, message: 'Name is required' }]}
          isNotDisplay
        />
        <Form.Item
          name="type"
          label="Type"
          rules={[{ required: true, message: 'Type is required' }]}
          labelCol={formLabelStyle}
          style={{ marginBottom: 0 }}
        >
          <Select
            showSearch
            options={translatedTypes}
            optionFilterProp="label"
          />
        </Form.Item>
        { !!subTypes?.length && (
          <Form.Item
            name="subType"
            label="Sub-type"
            rules={[{ required: true, message: 'Sub-type is required' }]}
            labelCol={formLabelStyle}
            style={{ marginBottom: 0 }}
          >
            <Select options={subTypes} />
          </Form.Item>
        )}
        {
          selectedType
            && (selectedType === 'project' || isProjectTypeChange)
            && (
            <Form.Item
              name="projectTypes"
              label={`${t('Project')} Types`}
              labelCol={formLabelStyle}
              style={{ marginBottom: 0 }}
              rules={[{ required: isProjectTypeChange, message: `${t('Project')} Types is required` }]}
            >
              <Select
                mode="multiple"
                placeholder={isProjectTypeChange ? 'Select types' : 'All'}
                options={projectTypeOptions}
              />
            </Form.Item>
            )
        }
        {selectedType && selectedType !== 'moveBoard' && selectedType !== 'copyCard' && selectedType !== 'createCustomerProject' && (
          <Form.Item
            name="initialStatusId"
            label={statusLabel}
            rules={[{ required: true, message: `${statusLabel} is required` }]}
            labelCol={formLabelStyle}
            style={{ marginBottom: 0 }}
          >
            <Select
              showSearch
              options={statusOptions}
              optionFilterProp="label"
            />
          </Form.Item>
        )}
        {
          isTimeTriggerType(selectedType)
          && (!!dateFieldOptions.length || useDateField)
          && (
          <Form.Item
            name="useDateField"
            style={{ marginBottom: 0, marginTop: 10 }}
          >
            <OnTraccrSwitch title="Link to a card date field" />
          </Form.Item>
          )
        }
        {
          isTimeTriggerType(selectedType) && useDateField
          && (
          <Form.Item
            name="dateFieldId"
            label="Date Field"
            rules={[
              { required: true, message: 'Date Field is required' },
              {
                validator: () => (isDateFieldMissing
                  ? Promise.reject(new Error(MISSING_DATE_FIELD_MESSAGE))
                  : Promise.resolve()),
              },
            ]}
            validateStatus={isDateFieldMissing ? 'error' : undefined}
            help={isDateFieldMissing ? MISSING_DATE_FIELD_MESSAGE : undefined}
            labelCol={formLabelStyle}
            style={{ marginBottom: 0 }}
          >
            <Select
              options={dateFieldOptions}
              showSearch
              optionFilterProp="label"
            />
          </Form.Item>
          )
        }
        {
          isTimeTriggerType(selectedType) && !useDateField
          && (
          <Form.Item
            name="duration"
            label={durationLabel}
            rules={[{ required: true, message: `${durationLabel} is required` }]}
            labelCol={formLabelStyle}
            style={{ marginBottom: 0 }}
          >
            <DurationInput min={duration?.durationUnit === 'minutes' ? 15 : 1} />
          </Form.Item>
          )
        }
        {
          selectedType
          && selectedType !== 'moveBoard'
          && selectedType !== 'statusTasks'
          && selectedType !== 'zapierMove'
          && (!isTimeTriggerType(selectedType) || selectedSubType === 'changeCardColor')
          && selectedType !== 'copyCard'
          && selectedType !== 'createCustomerProject'
          && selectedType !== 'statusCustomerProject'
          && (
          <Form.Item
            name="color"
            label="Initial Color"
            rules={[{ required: true, message: 'Initial Color is required' }]}
            labelCol={formLabelStyle}
            style={{ marginBottom: 0, maxWidth: 250 }}
          >
            <BoardColorPicker isNotDisplay boardColors={selectedBoard?.colors} />
          </Form.Item>
          )
        }
        {
          selectedType
            && selectedType !== 'moveBoard'
            && selectedType !== 'statusTasks'
            && !isTimeTriggerType(selectedType)
            && selectedType !== 'zapierMove'
            && selectedType !== 'copyCard'
            && selectedType !== 'createCustomerProject'
            && selectedType !== 'statusCustomerProject'
          && (
          <Form.Item
            name="mappings"
            label="Mapping"
            rules={[{ required: true, message: 'Type is required' }]}
            style={{ marginTop: 15 }}
          >
            <Table
              dataSource={ourFields}
              columns={columns}
              pagination={false}
              scroll={{ y: 'calc(100vh - 200px)' }}
            />
          </Form.Item>
          )
        }
        {(selectedType === 'moveBoard' || selectedType === 'copyCard') && (
          <BoardMoveWorkflowConfigure
            currentStatuses={currentStatuses}
            targetBoardId={targetBoardId}
            cardTypeId={cardTypeId}
            keepColor={keepColor}
            isCopyCard={selectedType === 'copyCard'}
          />
        )}
        {
         (selectedType === 'statusTasks' || (isTimeTriggerType(selectedType) && selectedSubType === 'createTask'))
          && (
            <Form.Item
              name="tasks"
              label="Tasks"
              style={{ marginTop: 15 }}
            >
              <BoardWorkflowSubtasks
                cardTypeId={cardTypeId}
              />
            </Form.Item>
          )
        }
        {
          isTimeTriggerType(selectedType) && selectedSubType === 'sendEmail'
          && (
            <Form.Item
              name="emails"
              label="Emails"
              style={{ marginTop: 15 }}
            >
              <UserAssignmentSelector
                selected={selectedEmails}
                mode="tags"
                showPositions
                type="email"
                text="Select Recipient(s):"
                tokenSeparators={tokenSeparators}
              />
            </Form.Item>
          )
        }
        {(selectedType === 'createCustomerProject' || selectedType === 'statusCustomerProject') && (
        <>
        <Form.Item
          name="createCustomer"
          style={{ marginBottom: 0, paddingLeft: 5 }}
        >
          <OnTraccrCheckbox
            label="Create Customer?"
          />
        </Form.Item>
        <Form.Item
          name="createProject"
          style={{ marginBottom: 0, paddingLeft: 5 }}
        >
          <OnTraccrCheckbox label="Create Project?" />
        </Form.Item>
        </>
        )}
        {
          !!form.getFieldValue('createCustomer') && (
            <Form.Item
              name="customerMappings"
              label="Customer Field Mappings"
              rules={[{ required: form.getFieldValue('createCustomer'), message: 'Customer Mappings are required' }]}
              style={{ marginTop: 15 }}
            >
              <Table
                dataSource={CUSTOMER_FIELDS}
                columns={customerColumns}
                pagination={false}
                scroll={{ y: 'calc(100vh - 200px)' }}
              />
            </Form.Item>
          )
        }
        {
          !!form.getFieldValue('createProject') && (
            <>
              <Form.Item
                name="projectFileTemplateId"
                label="Project File Template"
                labelCol={formLabelStyle}
                style={{ marginBottom: 15 }}
              >
                <Select
                  options={fileTemplateOptions}
                  placeholder="Select a file template (optional)"
                  allowClear
                  showSearch
                  optionFilterProp="label"
                />
              </Form.Item>
              <Form.Item
                name="createProjectTypeId"
                label={`${t('Project')} Type`}
                labelCol={formLabelStyle}
                style={{ marginBottom: 15 }}
              >
                <Select
                  options={projectTypeOptions}
                  placeholder="Select a project type"
                  allowClear
                  showSearch
                  optionFilterProp="label"
                />
              </Form.Item>
              <Form.Item
                name="projectMappings"
                label="Project Field Mappings"
                rules={[{ required: form.getFieldValue('createProject'), message: 'Project Mappings are required' }]}
                style={{ marginTop: 15 }}
              >
                <Table
                  dataSource={PROJECT_FIELDS}
                  columns={projectColumns}
                  pagination={false}
                  scroll={{ y: 'calc(100vh - 200px)' }}
                />
              </Form.Item>
            </>
          )
        }
      </Form>
      <DrawerSubmitFooter
        onClose={onClose}
        onSubmit={onSubmitClicked}
        canSubmit={canSubmit}
      />
    </Drawer>
  );
}

BoardWorkflowAddDrawer.propTypes = {
  visible: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  cardTypeId: PropTypes.number.isRequired,
  selectedWorkflow: PropTypes.shape({
    type: PropTypes.string,
    name: PropTypes.string,
    mappings: PropTypes.object, // eslint-disable-line react/forbid-prop-types
    initialStatusId: PropTypes.number,
    color: PropTypes.string,
    keepColor: PropTypes.bool,
    createShadow: PropTypes.bool,
    createCustomer: PropTypes.bool,
    createProject: PropTypes.bool,
    customerMappings: PropTypes.object, // eslint-disable-line react/forbid-prop-types
    projectMappings: PropTypes.object, // eslint-disable-line react/forbid-prop-types
    projectFileTemplateId: PropTypes.string,
    projectTypes: PropTypes.arrayOf(PropTypes.string),
    dateFieldId: PropTypes.string,
  }),
  currentStatuses: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      title: PropTypes.string,
    }),
  ),
};

BoardWorkflowAddDrawer.defaultProps = {
  visible: false,
  selectedWorkflow: null,
  currentStatuses: [],
};

export default BoardWorkflowAddDrawer;
